import React from 'react';
import { getAuthHeaders } from '../../globals.js';

// Shared pieces of the Diagnostic tab: styles, Field, the admin API call and
// error messages. Errors never show Airtable details: the API only returns codes.

export const inputSt = {
  width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--globant-border)',
  background: 'var(--globant-input)', color: 'var(--globant-text)', fontSize: 14,
};
export const textareaSt = { ...inputSt, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' };
export const labelSt = { fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, display: 'block' };
export const cardSt = { background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 16, padding: 20, marginBottom: 16 };
export const mutedSt = { fontSize: 12, color: 'var(--globant-muted)' };
export const badgeSt = (on) => ({ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: on ? 'rgba(91,191,181,0.15)' : 'rgba(30,42,44,0.06)', color: on ? '#155C54' : 'var(--globant-muted)', whiteSpace: 'nowrap' });
export const warnSt = { ...cardSt, borderColor: 'rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.06)' };
export const tableSt = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
export const thSt = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--globant-border)', color: 'var(--globant-muted)', fontWeight: 700, whiteSpace: 'nowrap' };
export const tdSt = { padding: '6px 8px', borderBottom: '1px solid var(--globant-border)', verticalAlign: 'top' };

export function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={labelSt}>{label}</span>
      {children}
      {hint && <span style={{ ...mutedSt, fontSize: 11, display: 'block', marginTop: 3 }}>{hint}</span>}
    </label>
  );
}

const ERROR_LABELS = {
  unauthorized: 'Tu sesión venció. Vuelve a iniciar sesión.',
  forbidden: 'Solo el administrador de Oike puede usar esta pestaña.',
  not_found: 'No se encontró el registro. Actualiza la página.',
  too_large: 'El contenido es demasiado grande para enviarlo.',
  server_error: 'No se pudo completar. Vuelve a intentarlo en un momento.',
  'invalid_input: missing evidence': 'Cada puntaje necesita su evidencia.',
  'invalid_input: missing transcript': 'Pega el texto de la transcripción.',
  'invalid_input: invalid metrics': 'Las métricas del CRM no tienen el formato esperado.',
  'invalid_input: metrics too large': 'Las métricas del CRM son demasiado grandes para guardarlas.',
  'invalid_input: missing validation': 'Marca como validadas todas las filas antes de aprobar.',
  'invalid_input: missing reason': 'Escribe el porqué en cada nivel que cambiaste o que la IA marcó para revisar.',
  'invalid_input: missing channel': 'Elige el canal donde rinde más antes de aprobar.',
  'invalid_input: missing priorities': 'Cada prioridad necesita título y servicio.',
  'invalid_input: missing service': 'Elige y confirma el siguiente servicio antes de aprobar.',
  'invalid_input: not current': 'Esta ya no es la versión vigente. Actualiza el panel.',
  'invalid_input: not draft': 'Esta versión ya fue aprobada.',
  'invalid_input: downstream approved': 'No se puede reabrir: el paso siguiente ya está aprobado sobre esta versión.',
  'invalid_input: not approved': 'Solo se puede publicar una versión aprobada. Un borrador nunca se publica.',
  'invalid_input: published': 'Esta versión tiene un link publicado. Primero revócalo.',
  'invalid_input: stale': 'Este borrador se generó con datos que cambiaron después. Vuelve a correrlo antes de editarlo o aprobarlo.',
  'invalid_input: closed': 'El diagnóstico está cerrado: ya no se puede cambiar.',
  'invalid_input: not delivered': 'Primero publica el informe del founder: el cierre cuenta desde la entrega.',
  'invalid_input: missing confirmation': 'Falta confirmar.',
  'invalid_input: missing content': 'El texto está vacío.',
  'invalid_input: has approved p2': 'No se puede excluir: sus niveles (P2) ya están aprobados y entran en el informe.',
  'invalid_input: name mismatch': 'El nombre no coincide. No se borró nada.',
  'invalid_input: not test': 'Solo se pueden borrar diagnósticos de prueba.',
  'invalid_input: p4 not approved': 'Primero valida y aprueba las dimensiones (P4).',
  'invalid_input: missing country': 'No se pudo detectar el país del cliente. Elígelo y vuelve a intentarlo.',
  'invalid_input: no people': 'No hay personas evaluadas para calcular el precio.',
  'invalid_input: no account': 'El diagnóstico no está vinculado a una cuenta.',
  needs_confirmation: 'Hay avisos de los controles automáticos sin confirmar.',
  person_exists: 'Ya hay una ficha de persona con ese email.',
  // Preparing the start of a service (docs/servicios/inicio.md)
  origin_fields_missing: 'Faltan los campos de origen en Airtable (Service Origins y Origin Quotes en Diagnostics, Service Origin en Coaching Processes). Pedíselos a Omni: docs/servicios/inicio.md.',
  'invalid_input: quote not accepted': 'La propuesta no está aceptada: solo una propuesta ganada prepara el inicio de un servicio.',
  'invalid_input: line not found': 'La propuesta cambió desde que abriste el panel: volvé a abrirlo.',
  'invalid_input: destination not allowed': 'Esa línea no puede iniciarse en ese módulo.',
  'invalid_input: already linked to another line': 'Ese proceso ya está vinculado a otra línea de una propuesta.',
  'invalid_input: quote without account': 'La propuesta no tiene empresa: un diagnóstico necesita una.',
  'invalid_input: diagnostic not in account': 'Ese diagnóstico no es de la empresa de la propuesta.',
  person_field_missing: 'Falta el campo Person Id en Diagnostic Participants (Airtable). Pídeselo a Omni antes de elegir fichas.',
  'invalid_input: person already in diagnostic': 'Esa persona ya participa en este diagnóstico.',
  'invalid_input: delivered': 'El informe ya se entregó: los datos de este diagnóstico se conservan como estaban.',
  'invalid_input: person has another contact': 'Esa ficha ya está vinculada a otro contacto.',
  'invalid_input: contact already linked': 'Ese contacto ya tiene una ficha.',
  'invalid_input: missing name': 'Falta el nombre.',
  'invalid_input: invalid email': 'El email no es válido.',
  'invalid_input: invalid goal': 'La meta tiene que ser un monto mayor que 0 y un plazo de 1 a 60 meses.',
  'invalid_input: invalid stage map': 'Elige etapas del CRM, con la de reunión antes (o igual) que la de oportunidad.',
  'invalid_input: invalid rate': 'La conversión de contacto a reunión tiene que estar entre 0 y 100 %.',
  'invalid_input: invalid modules': 'Hay un módulo que no existe.',
  'invalid_input: missing modules': 'Elige al menos un módulo.',
  'invalid_input: rubric changed': 'La rúbrica cambió desde que se aprobó P4: la medición final no se puede aprobar con otra versión.',
  'invalid_input: invalid industry': 'Elige una industria de la lista.',
  'invalid_input: invalid country': 'Elige un país de la lista.',
};

// Why an AI step cannot run (diagnostic-admin answers 409 { error: 'ai_blocked', reason })
const AI_BLOCKED = {
  ai_disabled: 'La IA solo corre en diagnósticos de prueba hasta que DIAGNOSTIC_AI_REAL_CLIENTS sea "true".',
  running: 'Ese paso ya está corriendo.',
  trigger_failed: 'No se pudo iniciar la corrida. Vuelve a intentarlo.',
  published: 'Esa versión tiene un link publicado: revócalo antes de volver a correrla.',
  closed: 'El diagnóstico está cerrado.',
  p4_not_approved: 'Primero valida y aprueba las dimensiones (P4).',
  modules_not_confirmed: 'Primero confirma los módulos del System Design.',
  module_not_selected: 'Ese módulo no está entre los elegidos.',
  no_approved_modules: 'Hace falta al menos un módulo aprobado.',
  rubric_changed: 'La rúbrica cambió desde que se aprobó P4: la medición final no se puede hacer con otra versión.',
  client_context_missing: 'Falta algún dato del contexto del cliente (industria, tamaño, tipo de venta o país). Complétalo en la tarjeta "Contexto del cliente" antes de generar.',
};

export function errorMessage(data, status) {
  const code = data?.error || '';
  if (code === 'transcript_too_long') {
    return `La transcripción tiene ${fmtInt(data.characters)} caracteres y el máximo es ${fmtInt(data.max)}. Recórtala (por ejemplo, sin la parte de cierre) y vuelve a cargarla.`;
  }
  if (code === 'ai_blocked') return AI_BLOCKED[data.reason] || 'Ese paso todavía no está habilitado.';
  if (ERROR_LABELS[code]) return ERROR_LABELS[code];
  if (code.startsWith('invalid_input')) return 'Hay un dato inválido. Revisa los campos y vuelve a intentarlo.';
  if (status === 403) return ERROR_LABELS.forbidden;
  return ERROR_LABELS.server_error;
}

export async function adminCall(body) {
  const res = await fetch('/api/diagnostic-admin', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(errorMessage(data, res.status));
    err.code = data?.error || ''; // e.g. 'invalid_input: missing country', to react to it
    err.data = data;
    throw err;
  }
  return data;
}

export const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString('es') : '—');
export const fmtPct = (r) => (typeof r === 'number' ? `${Math.round(r * 100)}%` : '—');
export const fmtNum = (n, d = 0) => (typeof n === 'number' ? n.toLocaleString('es', { maximumFractionDigits: d }) : '—');
export const fmtDate = (iso, withTime = false) => {
  if (!iso) return '—';
  // A calendar day ("2026-10-26") is the same day everywhere: never shift it by the browser's zone
  const dayOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(iso));
  const d = new Date(dayOnly ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return '—';
  if (dayOnly) return d.toLocaleDateString('es', { dateStyle: 'medium', timeZone: 'UTC' });
  return withTime ? d.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' }) : d.toLocaleDateString('es', { dateStyle: 'medium' });
};

export function SubTabs({ tabs, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, borderBottom: '1px solid var(--globant-border)', paddingBottom: 8 }}>
      {tabs.map(([key, label]) => (
        <button key={key} className="action-btn" onClick={() => onChange(key)}
          style={{ fontSize: 12, padding: '6px 12px', ...(value === key ? { borderColor: 'var(--globant-green)', color: 'var(--globant-green)' } : {}) }}>
          {label}
        </button>
      ))}
    </div>
  );
}
